import {
    IDatasourceColumnSQLMap,
    IDatasourceColumnSQLMaps,
} from '../datasource-sql-map/datasource';
import {
    IQueryContextDatasourceColumn,
    IQueryContextDatasourceColumnState,
} from '../../services/query-context/models/IQueryContext';

/**
 * Builds an array of SQL filters or splits
 * @param biDimensions - bi dimensions
 * @param dataSourceDimensions - Data source dimensions
 * @param mapDimStateToSQL - Function that maps a dimension state to either a filter SQL or split SQL
 * @returns - Array of SQL filters or splits
 */
function buildSqlDimensionArray(
    biDimensions: IQueryContextDatasourceColumn[],
    dataSourceDimensions: IDatasourceColumnSQLMaps,
    mapDimStateToSQL: (
        dim: IDatasourceColumnSQLMap,
        state: IQueryContextDatasourceColumnState
    ) => string
): string[] {
    const missingDims: string[] = [];

    // Check if every item.ID in biDimensions is present in dataSourceDimensions
    biDimensions.forEach((dim) => {
        if (!(dim.id in dataSourceDimensions)) {
            missingDims.push(dim.id);
        }
    });

    // Throw error if any dimensions are missing
    if (missingDims.length > 0) {
        throw Error(
            `Dimension IDs not present in provided data source dimensions: ${missingDims}`
        );
    }

    // Build array of SQL filters or splits based on selected dimensions
    const sqlArray = biDimensions.map((dim) => {
        const dsDim = dataSourceDimensions[dim.id]!;
        const sql = mapDimStateToSQL(dsDim, dim.state);
        return sql;
    });

    return sqlArray;
}

/**
 * Builds an array of SQL filters
 * @returns - Array of SQL filters
 */
export function buildSqlFilters(
    biDimensions: IQueryContextDatasourceColumn[],
    dataSourceDimensions: IDatasourceColumnSQLMaps
): string[] {
    const filtersSQL = buildSqlDimensionArray(
        biDimensions,
        dataSourceDimensions,
        (dsDim, state) => dsDim.toFilterSQL!(state)
    );

    return filtersSQL;
}

/**
 * Builds an array of SQL splits
 * @returns - Array of SQL splits
 */
export function buildSqlSplits(
    datasourceColumn: IQueryContextDatasourceColumn[],
    datasourceColumnSQLMaps: IDatasourceColumnSQLMaps
): string[] {
    const splitsSQL = buildSqlDimensionArray(
        datasourceColumn,
        datasourceColumnSQLMaps,
        (dsDim, state) => dsDim.toSplitSQL!(state)
    );

    return splitsSQL;
}

/**
 * Constructs a SQL WHERE clause from an array of SQL statements.
 *
 * @param {string[]} sqlStatemets - An array of SQL statements to be joined.
 *
 * @returns {string} - A SQL WHERE clause. Returns an empty string if the input array is empty.
 *
 * @example
 * const whereClause = buildWhereSQL(["id=1", "name='John'"]);
 * // Outputs: "WHERE id=1 AND name='John'"
 */
export function buildWhereSQL(sqlStatemets: string[]): '' | string {
    let sql = '';
    if (sqlStatemets.length > 0) {
        sql = `WHERE ${sqlStatemets.join(' AND ')}`;
    }

    return sql;
}

export const SPLIT_GROUP_DIVIDER = ', ';

/**
 * Constructs a SQL split group clause from an array of SQL statements.
 *
 * @param {string[]} sqlStatements - An array of SQL statements to be used in the split group clause.
 * @param {string} alias - The alias to be used for the split group clause. Defaults to 'split_group'.
 * @param {boolean} endComma - Whether to end the clause with a comma. Defaults to true.
 *
 * @returns {string} - A SQL split group clause. Returns an empty string if the input array is empty.
 * If the input array has a single item, it returns that item followed by 'as split_group,'.
 * If the input array has multiple items, it casts each item as a string, concatenates them using the ARRAY_TO_STRING function with a specified divider  (', '), and then follows with 'as {alias},'.
 *
 * @example
 * const splitGroupClauseSingle = buildSplitGroupSQL(["name"]);
 * // Outputs: "CAST(name AS STRING) as split_group,"
 *
 * const splitGroupClauseSingle = buildSplitGroupSQL(["name"], "group");
 * // Outputs: "CAST(name AS STRING) as group,"
 *
 * const splitGroupClauseMultiple = buildSplitGroupSQL(["name", "age"], "group");
 * // Outputs: "ARRAY_TO_STRING([CAST(name AS STRING), CAST(age AS STRING)], ', ') as group,"
 */
export function buildSplitGroupSQL(
    sqlStatements: string[],
    alias: string = 'split_group',
    endComma: boolean = true
): '' | string {
    let sql = '';
    const comma = endComma ? ',' : '';
    if (sqlStatements.length == 1) {
        sql = `CAST(${sqlStatements[0]} AS STRING) AS ${alias}${comma}
    `;
    }
    if (sqlStatements.length > 1) {
        sql = `ARRAY_TO_STRING([${sqlStatements
            .map((s) => `CAST(${s} AS STRING)`)
            .join(', ')}], '${SPLIT_GROUP_DIVIDER}', '') AS ${alias}${comma}
    `;
    }

    return sql;
}

/**
 * Splits a string into an array using the SPLIT_GROUP_DIVIDER as the separator.
 *
 * @param splitGroup - The string to be split.
 *
 * @returns An array of split parts.
 */
export function splitGroupStringToArr(splitGroup: string): string[] {
    return splitGroup.split(SPLIT_GROUP_DIVIDER);
}
